import React, { useRef, useState, useEffect } from "react";
import { Modal, Input, Button } from "antd";
import { post } from "../services/services";
import { ErrorToast, SuccessToast } from "../widgets/AppToast";

const SecurityCodeModal = ({
  visible,
  onCancel,
  setModalVisible,
  apiEndPoint,
  onSuccess,
  record_id,
}) => {
  const [otp, setOtp] = useState(new Array(6).fill(""));
  const inputRefs = useRef([]);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [lockTimer, setLockTimer] = useState(0);

  const LOCK_KEY = `security_code_lock_time_${record_id}`;

  useEffect(() => {
    if (visible && record_id) {
      const lockTime = localStorage.getItem(LOCK_KEY);
      if (lockTime) {
        const elapsed = Math.floor((Date.now() - parseInt(lockTime, 10)) / 1000);
        if (elapsed < 300) {
          setIsLocked(true);
          setLockTimer(300 - elapsed);
        } else {
          localStorage.removeItem(LOCK_KEY);
        }
      }
    }
  }, [visible, record_id]);

  useEffect(() => {
    let interval = null;
    if (isLocked && lockTimer > 0) {
      interval = setInterval(() => {
        setLockTimer((prev) => {
          if (prev <= 1) {
            setIsLocked(false);
            setFailedAttempts(0);
            localStorage.removeItem(LOCK_KEY);
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isLocked, lockTimer]);

  const handleChange = (e, index) => {
    const value = e.target.value;
    if (/^[0-9]?$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      if (value && index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    const otp_code = otp.join("");
    if (otp_code.length !== 6) {
      ErrorToast("Invalid Security Code", "Please enter all 6 digits.");
      return;
    }

    if (isLocked) {
      ErrorToast("Locked", `Please wait ${lockTimer} seconds before retrying.`);
      return;
    }

    try {
      const FORM_DATA = new FormData();
      FORM_DATA.append("security_code", otp_code);
      FORM_DATA.append("record_id", record_id);

      const response = await post(apiEndPoint, FORM_DATA);

      if (response?.status === true) {
        SuccessToast("Success", response?.message || "Log submitted successfully.");
        setOtp(new Array(6).fill(""));
        setFailedAttempts(0);

        if (onSuccess) {
          onSuccess();
        } else {
          setModalVisible(false);
        }
      } else {
        const newAttempts = failedAttempts + 1;
        setFailedAttempts(newAttempts);
        if (newAttempts >= 3) {
          const lockTime = Date.now();
          localStorage.setItem(LOCK_KEY, lockTime.toString());
          setIsLocked(true);
          setLockTimer(300);
          ErrorToast("Too Many Attempts", "Locked for 5 minutes.");
        } else {
          ErrorToast("Error", response?.message || "OTP verification failed.");
        }
      }
    } catch (error) {
      ErrorToast("Request Failed", "An error occurred while submitting the code.");
    }
  };

  const handleCancel = () => {
    setOtp(new Array(6).fill(""));
    onCancel();
  };

  return (
    <Modal
      title="Please Enter Security Code"
      open={visible}
      onCancel={handleCancel}
      footer={null}
      destroyOnClose
    >
      <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
        {otp.map((digit, index) => (
          <Input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(e, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            style={{ width: 40, height: 40, textAlign: "center", fontSize: 18 }}
            inputMode="numeric"
            type="password"
            disabled={isLocked}
          />
        ))}
      </div>
      <div style={{ marginTop: 20, textAlign: "center" }}>
        <Button
          type="primary"
          onClick={handleSubmit}
          style={{ width: 150, color: isLocked ? "red" : "" }}
          disabled={isLocked}
        >
          {isLocked ? `Locked (${lockTimer}s)` : "Submit Log"}
        </Button>
      </div>
    </Modal>
  );
};

export default SecurityCodeModal;
